import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Sparkles, 
  Image as ImageIcon,
  DollarSign,
  GripVertical,
  Check,
  X,
  Loader2,
  FileText,
  Upload,
  Camera,
  Wand2,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { menuAPI, aiAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Menu Templates
const MENU_TEMPLATES = [
  {
    id: 'kebab',
    name: 'Kebab & Döner',
    icon: '🥙',
    categories: ['Döner', 'Kebab', 'Pita', 'Durum', 'Schotels', 'Friet', 'Dranken'],
    sampleItems: [
      { category: 'Döner', name: 'Döner Kebab', price: 8.50, description: 'Gesneden döner vlees met verse salade en saus' },
      { category: 'Döner', name: 'Döner Box', price: 9.50, description: 'Döner vlees op friet met salade en saus' },
      { category: 'Pita', name: 'Pita Döner', price: 7.50, description: 'Gevulde pita met döner vlees' },
      { category: 'Durum', name: 'Durum Döner', price: 8.00, description: 'Döner in een grote dürüm wrap' },
      { category: 'Schotels', name: 'Mix Grill Schotel', price: 16.50, description: 'Mix van verschillende vlees met rijst en salade' },
      { category: 'Friet', name: 'Friet', price: 3.50, description: 'Verse friet' },
      { category: 'Friet', name: 'Friet Speciaal', price: 5.00, description: 'Friet met mayo, curry en uitjes' },
      { category: 'Dranken', name: 'Coca-Cola', price: 2.50, description: '33cl' },
      { category: 'Dranken', name: 'Ayran', price: 2.00, description: 'Traditionele yoghurtdrank' },
    ]
  },
  {
    id: 'pizzeria',
    name: 'Pizzeria',
    icon: '🍕',
    categories: ['Pizza\'s', 'Pasta', 'Salades', 'Voorgerechten', 'Desserts', 'Dranken'],
    sampleItems: [
      { category: 'Pizza\'s', name: 'Margherita', price: 10.00, description: 'Tomaat, mozzarella, basilicum' },
      { category: 'Pizza\'s', name: 'Pepperoni', price: 12.50, description: 'Tomaat, mozzarella, pepperoni' },
      { category: 'Pizza\'s', name: 'Quattro Formaggi', price: 13.50, description: 'Vier kazen: mozzarella, gorgonzola, parmezaan, geitenkaas' },
      { category: 'Pasta', name: 'Spaghetti Bolognese', price: 12.00, description: 'Spaghetti met huisgemaakte vleessaus' },
      { category: 'Pasta', name: 'Penne Arrabiata', price: 11.00, description: 'Penne met pittige tomatensaus' },
      { category: 'Salades', name: 'Caprese', price: 8.50, description: 'Tomaat, mozzarella, basilicum, olijfolie' },
      { category: 'Desserts', name: 'Tiramisu', price: 6.50, description: 'Huisgemaakte tiramisu' },
      { category: 'Dranken', name: 'Chianti (glas)', price: 5.00, description: 'Italiaanse rode wijn' },
    ]
  },
  {
    id: 'chinese',
    name: 'Chinees',
    icon: '🥡',
    categories: ['Voorgerechten', 'Soepen', 'Rijst', 'Bami & Nasi', 'Vlees', 'Kip', 'Vis', 'Vegetarisch', 'Desserts'],
    sampleItems: [
      { category: 'Voorgerechten', name: 'Mini Loempia\'s (6st)', price: 5.50, description: 'Krokante loempia\'s met zoetzure saus' },
      { category: 'Soepen', name: 'Kippensoep', price: 4.50, description: 'Chinese kippensoep met groenten' },
      { category: 'Bami & Nasi', name: 'Bami Goreng', price: 10.50, description: 'Gebakken noedels met groenten' },
      { category: 'Bami & Nasi', name: 'Nasi Goreng', price: 10.50, description: 'Gebakken rijst met groenten' },
      { category: 'Kip', name: 'Kip Babi Pangang', price: 13.50, description: 'Kip in zoete rode saus' },
      { category: 'Vlees', name: 'Babi Pangang', price: 14.50, description: 'Geroosterd varkensvlees in zoete saus' },
      { category: 'Vis', name: 'Gamba\'s in Knoflook', price: 15.50, description: 'Grote garnalen in knoflooksaus' },
    ]
  },
  {
    id: 'frituur',
    name: 'Frituur',
    icon: '🍟',
    categories: ['Frieten', 'Snacks', 'Hamburgers', 'Broodjes', 'Sauzen', 'Dranken'],
    sampleItems: [
      { category: 'Frieten', name: 'Kleine Friet', price: 3.00, description: 'Verse friet' },
      { category: 'Frieten', name: 'Grote Friet', price: 4.00, description: 'Verse friet grote portie' },
      { category: 'Snacks', name: 'Frikandel', price: 2.00, description: 'Klassieke frikandel' },
      { category: 'Snacks', name: 'Kroket', price: 2.50, description: 'Vleeskroket' },
      { category: 'Snacks', name: 'Bitterballen (6st)', price: 5.00, description: 'Vleesballetjes' },
      { category: 'Hamburgers', name: 'Hamburger', price: 4.50, description: 'Hamburger met sla, tomaat, ui' },
      { category: 'Hamburgers', name: 'Cheeseburger', price: 5.00, description: 'Met extra kaas' },
      { category: 'Sauzen', name: 'Mayonaise', price: 0.50, description: '' },
      { category: 'Sauzen', name: 'Ketchup', price: 0.50, description: '' },
    ]
  },
];

export const MenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAIScannerModal, setShowAIScannerModal] = useState(false);
  const [fotosBezig, setFotosBezig] = useState(false);

  // Foto's die het systeem zelf heeft toegekend. Alleen die zijn te verwijderen
  // met de knop hieronder; wat je zelf hebt geüpload telt hier niet mee.
  const automatischeFotos = items.filter(
    (i) => i.image_source === 'stock' || i.image_source === 'ai').length;

  const ruimFotosOp = async () => {
    const metAI = items.filter((i) => i.image_source === 'ai').length;
    const stock = items.filter((i) => i.image_source === 'stock').length;
    if (!window.confirm(
      `Automatisch toegekende foto\u0027s verwijderen?\n\n`
      + `${stock} stockfoto\u0027s${metAI ? ` en ${metAI} AI-foto\u0027s` : ''} worden weggehaald.\n`
      + `Foto\u0027s die je zelf hebt geüpload blijven staan.\n\n`
      + `Dit kan niet ongedaan gemaakt worden.`
    )) return;
    setFotosBezig(true);
    try {
      const { data } = await menuAPI.cleanupMenuPhotos({ include_ai: true });
      toast.success(
        `${data.removed} foto\u0027s verwijderd.`
        + (data.remaining_with_photo
          ? ` ${data.remaining_with_photo} gerechten houden hun eigen foto.`
          : ''),
        { duration: 8000 });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opruimen mislukt. Probeer het opnieuw.');
    } finally {
      setFotosBezig(false);
    }
  };
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const { restaurant } = useAuth();

  const fetchData = async () => {
    try {
      const [categoriesRes, itemsRes] = await Promise.all([
        menuAPI.getCategories(),
        menuAPI.getItems(),
      ]);
      setCategories(categoriesRes.data);
      setItems(itemsRes.data);
      if (categoriesRes.data.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch menu data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteCategory = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    const itemCount = items.filter(i => i.category_id === categoryId).length;
    const confirmMsg = itemCount > 0
      ? `"${category?.name}" verwijderen? Ook de ${itemCount} bijhorende gerecht(en) worden permanent verwijderd.`
      : `"${category?.name}" verwijderen?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await menuAPI.deleteCategory(categoryId);
      toast.success('Categorie verwijderd');
      fetchData();
      if (selectedCategory === categoryId) {
        setSelectedCategory(categories.filter(c => c.id !== categoryId)[0]?.id || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon categorie niet verwijderen');
    }
  };

  const moveCategory = async (categoryId, direction) => {
    // direction: -1 (left) or +1 (right)
    const idx = categories.findIndex(c => c.id === categoryId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= categories.length) return;
    // Optimistic swap in local state
    const reordered = [...categories];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setCategories(reordered);
    try {
      await menuAPI.reorderCategories(reordered.map(c => c.id));
    } catch (_err) {
      toast.error('Kon volgorde niet opslaan');
      fetchData();
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Dit gerecht verwijderen?')) return;
    try {
      await menuAPI.deleteItem(itemId);
      toast.success('Gerecht verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon gerecht niet verwijderen');
    }
  };

  const filteredItems = items.filter(item => item.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Menu Builder</h1>
          <p className="text-gray-500">Beheer categorieën en menu items met AI ondersteuning</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Preview Webshop Button */}
          <Button 
            variant="outline"
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            onClick={() => window.open(`/shop/${restaurant?.slug}`, '_blank')}
            data-testid="preview-webshop-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Bekijk Webshop
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowTemplateModal(true)}
            data-testid="templates-btn"
          >
            <ChefHat className="w-4 h-4 mr-2" />
            Templates
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowAIScannerModal(true)}
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
            data-testid="ai-scanner-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            AI Menu Scanner
          </Button>
          {automatischeFotos > 0 && (
            <Button
              variant="outline"
              onClick={ruimFotosOp}
              disabled={fotosBezig}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="cleanup-photos-btn"
            >
              {fotosBezig
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <ImageIcon className="w-4 h-4 mr-2" />}
              {fotosBezig
                ? 'Bezig…'
                : `Automatische foto\u0027s verwijderen (${automatischeFotos})`}
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
            data-testid="add-category-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Categorie
          </Button>
          <Button 
            className="btn-primary"
            onClick={() => { setEditingItem(null); setShowItemModal(true); }}
            disabled={!selectedCategory}
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Item
          </Button>
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map((category, idx) => (
          <div
            key={category.id}
            className={`flex items-center gap-0.5 pl-2 pr-1 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
              selectedCategory === category.id
                ? 'bg-[#1A4D2E] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setSelectedCategory(category.id)}
            data-testid={`category-tab-${category.id}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedCategory(category.id)}
          >
            <button
              className={`p-1 rounded-full transition-opacity ${selectedCategory === category.id ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-300 text-gray-500'} ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
              onClick={(e) => { e.stopPropagation(); moveCategory(category.id, -1); }}
              title="Naar links verplaatsen"
              disabled={idx === 0}
              data-testid={`move-left-${category.id}`}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            {category.image_url && (
              <img src={category.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
            )}
            <span className="mx-1">{category.name}</span>
            <button
              className={`p-1 rounded-full transition-opacity ${selectedCategory === category.id ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-300 text-gray-500'} ${idx === categories.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
              onClick={(e) => { e.stopPropagation(); moveCategory(category.id, +1); }}
              title="Naar rechts verplaatsen"
              disabled={idx === categories.length - 1}
              data-testid={`move-right-${category.id}`}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
            <button
              className={`p-1 rounded-full hover:bg-white/20 ${selectedCategory === category.id ? 'text-white' : 'text-gray-500'}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditingCategory(category);
                setShowCategoryModal(true);
              }}
              title="Categorie bewerken"
              data-testid={`edit-category-${category.id}`}
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              className={`p-1 rounded-full hover:bg-red-500/30 ${selectedCategory === category.id ? 'text-white' : 'text-red-500'}`}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCategory(category.id);
              }}
              title="Categorie verwijderen"
              data-testid={`delete-category-${category.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Items Grid */}
      {categories.length === 0 ? (
        <div className="card-dashboard text-center py-12">
          <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">Nog geen menu items</h3>
          <p className="text-gray-500 mb-4">Begin met een kant-en-klare template of maak je eigen categorieën</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
              <ChefHat className="w-4 h-4 mr-2" />
              Kies een Template
            </Button>
            <Button onClick={() => setShowCategoryModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe Categorie
            </Button>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="card-dashboard text-center py-12">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">Geen items in deze categorie</h3>
          <p className="text-gray-500 mb-4">Voeg je eerste item toe aan deze categorie</p>
          <Button className="btn-primary" onClick={() => setShowItemModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Item Toevoegen
          </Button>
        </div>
      ) : (
        <div className="menu-grid">
          {filteredItems.map((item) => (
            <div key={item.id} className="card-menu-item" data-testid={`menu-item-${item.id}`}>
              {!item.image_url && (
                <div className="relative h-40 overflow-hidden bg-gray-50 flex flex-col items-center justify-center border-b border-gray-100">
                  <ImageIcon className="w-8 h-8 text-gray-300" />
                  <span className="text-xs text-gray-400 mt-1">Nog geen foto</span>
                </div>
              )}
              {item.image_url && (
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  {item.is_featured && (
                    <span className="featured-badge">Featured</span>
                  )}
                  {item.image_source && item.image_source !== 'upload' && (
                    <span
                      title={item.image_source === 'stock'
                        ? 'Automatisch gekozen stockfoto — vervang hem door je eigen foto'
                        : 'Door AI gegenereerd — vervang hem door je eigen foto'}
                      data-testid={`auto-foto-${item.id}`}
                      className="absolute bottom-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-black/60 text-white backdrop-blur-sm"
                    >
                      {item.image_source === 'stock' ? 'stockfoto' : 'AI-foto'}
                    </span>
                  )}
                  {!item.is_available && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-medium">Unavailable</span>
                    </div>
                  )}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-heading font-semibold text-gray-900">{item.name}</h3>
                  <span className="font-heading font-bold text-[#1A4D2E]">{formatPrice(item.price)}</span>
                </div>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {item.description || item.ai_generated_description || 'Geen beschrijving'}
                </p>
                <div className="flex items-center justify-end">
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setEditingItem(item); setShowItemModal(true); }}
                      data-testid={`edit-item-btn-${item.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteItem(item.id)}
                      data-testid={`delete-item-btn-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zolang er nog stockfoto's staan blijft de verwijzing naar Pexels
          verplicht. Zodra ze opgeruimd zijn, verdwijnt deze regel vanzelf. */}
      {filteredItems.some((i) => i.image_source === 'stock') && (
        <p className="text-xs text-gray-400 text-center" data-testid="pexels-credit">
          De automatisch gekozen foto&#39;s kwamen van{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer"
             className="underline hover:text-gray-600">Pexels</a>
          . Je kunt ze hierboven in een keer verwijderen.
        </p>
      )}

      {/* Category Modal */}
      <CategoryModal
        open={showCategoryModal}
        onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
        category={editingCategory}
        onSave={fetchData}
      />

      {/* Item Modal */}
      <ItemModal
        open={showItemModal}
        onClose={() => { setShowItemModal(false); setEditingItem(null); }}
        item={editingItem}
        categoryId={selectedCategory}
        cuisineType={restaurant?.cuisine_type}
        onSave={fetchData}
      />

      {/* Template Modal */}
      <TemplateModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onApply={fetchData}
      />

      {/* AI Scanner Modal */}
      <AIScannerModal
        open={showAIScannerModal}
        onClose={() => setShowAIScannerModal(false)}
        onApply={fetchData}
      />
    </div>
  );
};

const CategoryModal = ({ open, onClose, category, onSave }) => {
  const [formData, setFormData] = useState({ name: '', description: '', image_url: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({ name: category.name, description: category.description || '', image_url: category.image_url || '' });
    } else {
      setFormData({ name: '', description: '', image_url: '' });
    }
  }, [category]);

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Afbeelding te groot (max 3 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, image_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (category) {
        await menuAPI.updateCategory(category.id, formData);
        toast.success('Categorie bijgewerkt');
      } else {
        await menuAPI.createCategory(formData);
        toast.success('Categorie aangemaakt');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Kon categorie niet opslaan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? 'Categorie Bewerken' : 'Categorie Toevoegen'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category-name">Naam</Label>
            <Input
              id="category-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="bijv. Voorgerechten, Hoofdgerechten"
              required
              data-testid="category-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category-desc">Beschrijving (optioneel)</Label>
            <Input
              id="category-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Korte beschrijving van deze categorie"
              data-testid="category-desc-input"
            />
          </div>

          {/* Category image */}
          <div className="space-y-2">
            <Label>Afbeelding (optioneel)</Label>
            {formData.image_url && (
              <div className="relative w-full h-32 rounded-lg overflow-hidden border">
                <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-red-600 text-xs hover:bg-white shadow"
                  data-testid="remove-category-image-btn"
                  title="Verwijder afbeelding"
                >
                  ✕
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://... of gebruik upload knop rechts"
                value={formData.image_url.startsWith('data:') ? '' : formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                data-testid="category-image-url-input"
                className="flex-1"
              />
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm bg-gray-50 hover:bg-gray-100" data-testid="category-image-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageFile}
                  data-testid="category-image-file-input"
                />
                Upload
              </label>
            </div>
            <p className="text-xs text-gray-500">Verschijnt als achtergrond op de categorie-tegel in de webshop.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-category-btn">
              {loading ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ItemModal = ({ open, onClose, item, categoryId, cuisineType, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    is_available: true,
    is_featured: false,
  });
  const [loading, setLoading] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);

  const generateAIImage = async () => {
    if (!item?.id) {
      toast.error('Sla het item eerst op voordat je een foto genereert.');
      return;
    }
    if (!formData.name) {
      toast.error('Vul eerst een naam in.');
      return;
    }
    setGeneratingImage(true);
    try {
      const res = await menuAPI.generateImage(item.id);
      if (res.data?.image_url) {
        setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
        toast.success('Foto gegenereerd met AI!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Foto genereren mislukt. Probeer opnieuw.');
    } finally {
      setGeneratingImage(false);
    }
  };

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description || '',
        price: item.price.toString(),
        image_url: item.image_url || '',
        is_available: item.is_available,
        is_featured: item.is_featured,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        image_url: '',
        is_available: true,
        is_featured: false,
      });
    }
  }, [item]);

  const generateAIDescription = async () => {
    if (!formData.name) {
      toast.error('Voer eerst een itemnaam in');
      return;
    }
    setGeneratingDescription(true);
    try {
      const response = await aiAPI.generateDescription({
        item_name: formData.name,
        cuisine_type: cuisineType,
        price: parseFloat(formData.price) || null,
      });
      setFormData({ ...formData, description: response.data.description });
      toast.success('AI beschrijving gegenereerd!');
    } catch (error) {
      toast.error('Kon beschrijving niet genereren');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        category_id: categoryId,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image_url: formData.image_url,
        is_available: formData.is_available,
        is_featured: formData.is_featured,
      };
      
      if (item) {
        await menuAPI.updateItem(item.id, data);
        toast.success('Item bijgewerkt');
      } else {
        await menuAPI.createItem(data);
        toast.success('Item aangemaakt');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Kon item niet opslaan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{item ? 'Menu Item Bewerken' : 'Menu Item Toevoegen'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="space-y-2">
            <Label htmlFor="item-name">Naam *</Label>
            <Input
              id="item-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Grilled Ribeye Steak"
              required
              data-testid="item-name-input"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="item-desc">Beschrijving</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[#E07A5F]"
                onClick={generateAIDescription}
                disabled={generatingDescription}
                data-testid="generate-description-btn"
              >
                {generatingDescription ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1" />
                )}
                AI Beschrijving
              </Button>
            </div>
            <Textarea
              id="item-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Beschrijf je gerecht..."
              rows={3}
              data-testid="item-desc-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="item-price">Prijs (€) *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              <Input
                id="item-price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="pl-8"
                placeholder="0.00"
                required
                data-testid="item-price-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label htmlFor="item-image">Afbeelding</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={generateAIImage}
                disabled={generatingImage || !item?.id}
                data-testid="generate-ai-image-btn"
                className="text-xs h-7"
              >
                {generatingImage ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Genereren...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> AI foto genereren</>
                )}
              </Button>
            </div>
            {formData.image_url && (
              <div className="relative w-full">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="h-32 w-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/95 text-red-600 shadow font-bold hover:bg-white"
                  title="Verwijderen"
                  data-testid="remove-item-image-btn"
                >
                  ✕
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="item-image"
                  type="url"
                  value={formData.image_url?.startsWith('data:') ? '' : (formData.image_url || '')}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="pl-9"
                  placeholder="https://... of Upload / AI foto"
                  data-testid="item-image-input"
                />
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 hover:bg-gray-100 whitespace-nowrap" data-testid="item-image-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 3 * 1024 * 1024) {
                      toast.error('Afbeelding te groot (max 3 MB)');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => setFormData((prev) => ({ ...prev, image_url: reader.result }));
                    reader.readAsDataURL(file);
                  }}
                  data-testid="item-image-file-input"
                />
                <Upload className="w-4 h-4" />
                Upload
              </label>
            </div>
            {!item?.id && (
              <p className="text-xs text-gray-500 italic">Sla het item eerst op om een AI-foto te kunnen genereren.</p>
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="item-available">Beschikbaar</Label>
              <p className="text-sm text-gray-500">Toon dit item op je menu</p>
            </div>
            <Switch
              id="item-available"
              checked={formData.is_available}
              onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
              data-testid="item-available-switch"
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="item-featured">Uitgelicht</Label>
              <p className="text-sm text-gray-500">Markeer dit item als speciaal</p>
            </div>
            <Switch
              id="item-featured"
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              data-testid="item-featured-switch"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-item-btn">
              {loading ? 'Opslaan...' : 'Item Opslaan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Template Modal
const TemplateModal = ({ open, onClose, onApply }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    try {
      // Create categories
      for (const categoryName of selectedTemplate.categories) {
        try {
          await menuAPI.createCategory({ name: categoryName });
        } catch (e) {
          // Category might already exist
        }
      }
      
      // Get created categories
      const categoriesResponse = await menuAPI.getCategories();
      const categoriesMap = {};
      categoriesResponse.data.forEach(cat => {
        categoriesMap[cat.name] = cat.id;
      });
      
      // Create sample items
      for (const item of selectedTemplate.sampleItems) {
        const categoryId = categoriesMap[item.category];
        if (categoryId) {
          try {
            await menuAPI.createItem({
              category_id: categoryId,
              name: item.name,
              description: item.description,
              price: item.price,
              is_available: true,
            });
          } catch (e) {
            // Item might already exist
          }
        }
      }
      
      toast.success(`Template "${selectedTemplate.name}" toegepast!`);
      onApply();
      onClose();
    } catch (error) {
      toast.error('Kon template niet toepassen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Menu Template Kiezen</DialogTitle>
          <DialogDescription>
            Kies een kant-en-klare menu template om snel te starten
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 py-4">
          {MENU_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                selectedTemplate?.id === template.id
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{template.icon}</span>
                <span className="font-semibold text-gray-900">{template.name}</span>
              </div>
              <p className="text-sm text-gray-500 mb-2">
                {template.categories.length} categorieën, {template.sampleItems.length} items
              </p>
              <div className="flex flex-wrap gap-1">
                {template.categories.slice(0, 4).map((cat, idx) => (
                  <span key={idx} className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                    {cat}
                  </span>
                ))}
                {template.categories.length > 4 && (
                  <span className="text-xs text-gray-400">+{template.categories.length - 4}</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button 
            className="btn-primary" 
            onClick={handleApplyTemplate} 
            disabled={!selectedTemplate || loading}
          >
            {loading ? 'Toepassen...' : 'Template Toepassen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// AI Scanner Modal
const AIScannerModal = ({ open, onClose, onApply }) => {
  const [mode, setMode] = useState('text'); // 'text' or 'image'
  const [menuText, setMenuText] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [step, setStep] = useState(1); // 1: input, 2: review

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Alleen JPEG, PNG of WebP afbeeldingen zijn toegestaan');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Afbeelding mag maximaal 5MB zijn');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setImagePreview(base64);
      // Remove the data:image/...;base64, prefix for the API
      setImageBase64(base64.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (mode === 'text' && !menuText.trim()) {
      toast.error('Voer menu tekst in');
      return;
    }
    if (mode === 'image' && !imageBase64) {
      toast.error('Upload een menu afbeelding');
      return;
    }
    
    setLoading(true);
    try {
      const response = await aiAPI.scanMenu({
        mode,
        text: mode === 'text' ? menuText : null,
        image_base64: mode === 'image' ? imageBase64 : null,
      });
      
      if (response.data.items && response.data.items.length > 0) {
        setParsedItems(response.data.items);
        setStep(2);
        toast.success(`${response.data.items.length} items gevonden!`);
      } else {
        toast.error('Geen items gevonden. Probeer een duidelijkere afbeelding of beschrijving.');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Kon menu niet scannen. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyItems = async () => {
    setLoading(true);
    try {
      // Group items by category
      const categories = [...new Set(parsedItems.map(item => item.category || 'Overig'))];
      
      // Create categories first
      for (const catName of categories) {
        try {
          await menuAPI.createCategory({ name: catName });
        } catch (e) {}
      }
      
      // Get category IDs
      const categoriesResponse = await menuAPI.getCategories();
      const categoriesMap = {};
      categoriesResponse.data.forEach(cat => {
        categoriesMap[cat.name] = cat.id;
      });
      
      // Create items
      for (const item of parsedItems) {
        const categoryId = categoriesMap[item.category || 'Overig'];
        if (categoryId) {
          try {
            await menuAPI.createItem({
              category_id: categoryId,
              name: item.name,
              description: item.description || '',
              price: parseFloat(item.price) || 0,
              is_available: true,
            });
          } catch (e) {}
        }
      }
      
      toast.success(`${parsedItems.length} items toegevoegd!`);
      onApply();
      onClose();
      setStep(1);
      setMenuText('');
      setImageBase64('');
      setImagePreview('');
      setParsedItems([]);
    } catch (error) {
      toast.error('Kon items niet toevoegen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setStep(1); setParsedItems([]); setImageBase64(''); setImagePreview(''); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            AI Menu Scanner
          </DialogTitle>
          <DialogDescription>
            Laat AI je menu automatisch inlezen en items aanmaken
          </DialogDescription>
        </DialogHeader>
        
        {step === 1 ? (
          <>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setMode('text')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                  mode === 'text' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                }`}
              >
                <FileText className={`w-5 h-5 mx-auto mb-1 ${mode === 'text' ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">Tekst invoeren</span>
              </button>
              <button
                onClick={() => setMode('image')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                  mode === 'image' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                }`}
              >
                <Camera className={`w-5 h-5 mx-auto mb-1 ${mode === 'image' ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">Foto uploaden</span>
              </button>
            </div>

            {mode === 'text' ? (
              <div className="space-y-2">
                <Label>Menu tekst</Label>
                <Textarea
                  value={menuText}
                  onChange={(e) => setMenuText(e.target.value)}
                  placeholder={`Plak je menu hier, bijvoorbeeld:

VOORGERECHTEN
Tomatensoep - €5,50
Garnalencocktail - €8,50

HOOFDGERECHTEN  
Biefstuk met friet - €18,50
Pasta Carbonara - €14,00`}
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="menu-text-input"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <Label>Menu foto uploaden</Label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                    imagePreview ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
                  }`}
                >
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={imagePreview} 
                        alt="Menu preview" 
                        className="max-h-48 mx-auto rounded-lg shadow-md"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setImageBase64(''); setImagePreview(''); }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Verwijderen
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="menu-image-input"
                      />
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 font-medium">
                        Klik om een foto te uploaden
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        JPEG, PNG of WebP (max 5MB)
                      </p>
                    </label>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuleren</Button>
              <Button 
                onClick={handleScan}
                disabled={loading || (mode === 'text' && !menuText.trim()) || (mode === 'image' && !imageBase64)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="scan-menu-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scannen...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Menu Scannen
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="py-4">
              <p className="text-sm text-gray-500 mb-4">
                {parsedItems.length} items gevonden. Controleer en pas aan indien nodig:
              </p>
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {parsedItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">{item.category}</p>
                    </div>
                    <span className="font-medium text-emerald-600">€{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Terug</Button>
              <Button 
                onClick={handleApplyItems}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Toevoegen...' : `${parsedItems.length} Items Toevoegen`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

